// The query tab's view of a manual IntelliSense refresh — both the pill's
// "Reload schema" button (S7 item 5) and any refresh started ELSEWHERE for
// this tab's connection + database (Ctrl+Shift+R, the Command Palette, the
// Object Explorer's Refresh, another tab's pill).
//
// The refresh itself is `refreshIntelliSense` (schemaRefresh/), which records
// its in-flight window in `schemaRefreshStore`; this hook mirrors that window
// onto the tab's pill (`initializing` with a "Refreshing IntelliSense…"
// message while it runs) and re-derives the pill state when it ends, so a
// refresh started from the explorer is visible in every open tab on that
// database, not just where it was clicked.
//
// Every outcome is fenced by an EPOCH: the epoch advances whenever the pair
// the hook tracks changes, whenever a window the hook did not open starts,
// and whenever any window ends. A reload the pill started applies its result
// only while its epoch is current — so a reload abandoned by a disconnect
// that settles late (during, or even after, a successor refresh of the same
// pair) can neither touch the pill nor be picked up by the successor's end
// transition.

import { useCallback, useEffect, useRef } from 'react';

import type { DatabaseEngineId } from '../../../../core/engines';
import { resolveSchemaReadyStatus } from '../../../connections/services/language-services/introspectionTruncation';
import { isConnectionActive } from '../../../connections/utils/connectionLookup';
import { refreshIntelliSense } from '../../../intellisense/schemaRefresh/refreshIntelliSense';
import {
  isSchemaRefreshing,
  lastSchemaRefreshError,
  schemaRefreshGeneration,
  useIsSchemaRefreshing,
} from '../../../intellisense/schemaRefresh/schemaRefreshStore';
import type { IntelliSenseStatusType } from '../../common/utils/sqlMonacoTypes';

export const INTELLISENSE_REFRESHING_MESSAGE = 'Refreshing IntelliSense…';

export type UseIntelliSenseSchemaReloadParams = {
  connectionId?: string;
  database?: string;
  engineId: DatabaseEngineId;
  /** The tab's IntelliSense status setter — same one SqlMonaco status events feed. */
  onStatusChange: (status: IntelliSenseStatusType, message?: string) => void;
};

export type UseIntelliSenseSchemaReloadResult = {
  reloadSchema: () => void;
  /** True while a refresh for this tab's connection + database is in flight, whoever started it. */
  isReloadingSchema: boolean;
};

type FinalStatus = { status: IntelliSenseStatusType; message?: string };

function pairOf(connectionId?: string, database?: string): string | undefined {
  return connectionId && database ? `${connectionId}|${database}` : undefined;
}

/** The pill state once a refresh of the pair has ended. */
function statusAfterRefresh(connectionId: string, database: string): FinalStatus {
  // The window can also close because the connection went away (the
  // connection store clears the pair's entry on disconnect): that is not a
  // finished refresh, and the pill must not claim a schema for a closed
  // connection.
  if (!isConnectionActive(connectionId)) return { status: 'unavailable' };
  // A failed refetch is remembered by the store (the truncation record only
  // knows about clipped / failed ARTEFACTS, and Kusto records nothing there).
  const error = lastSchemaRefreshError(connectionId, database);
  if (error) return { status: 'error', message: error };
  // Re-read the truncation record the refetch just rebuilt: still clipped →
  // `partial` (with the current artefact list), fits now → `ready`.
  return resolveSchemaReadyStatus(connectionId, database);
}

export function useIntelliSenseSchemaReload({
  connectionId,
  database,
  engineId,
  onStatusChange,
}: UseIntelliSenseSchemaReloadParams): UseIntelliSenseSchemaReloadResult {
  const isReloadingSchema = useIsSchemaRefreshing(connectionId, database);
  const wasReloadingRef = useRef(false);
  const pairRef = useRef<string | undefined>(undefined);
  const epochRef = useRef(0);
  // Set by `reloadSchema` so the start transition of the window IT opened
  // does not advance the epoch past its own run.
  const ownStartPendingRef = useRef(false);
  // The outcome the reload THIS hook started already applied to the pill.
  // The store's end-of-refresh transition and the promise settling race each
  // other, so whichever lands second re-applies the same final state instead
  // of overwriting an `error` with a re-derived `ready`.
  const finalStatusRef = useRef<FinalStatus | null>(null);

  useEffect(() => {
    const pair = pairOf(connectionId, database);
    if (pair !== pairRef.current) {
      // New pair: start tracking it as it is, with no transition and no
      // outcome carried over from the previous pair.
      pairRef.current = pair;
      wasReloadingRef.current = isReloadingSchema;
      finalStatusRef.current = null;
      ownStartPendingRef.current = false;
      epochRef.current += 1;
      if (isReloadingSchema) onStatusChange('initializing', INTELLISENSE_REFRESHING_MESSAGE);
      return;
    }
    const was = wasReloadingRef.current;
    wasReloadingRef.current = isReloadingSchema;
    if (isReloadingSchema && !was) {
      if (ownStartPendingRef.current) {
        ownStartPendingRef.current = false;
      } else {
        // A window opened by ANOTHER surface: whatever this hook's own
        // reload still owes is obsolete.
        epochRef.current += 1;
        finalStatusRef.current = null;
      }
      onStatusChange('initializing', INTELLISENSE_REFRESHING_MESSAGE);
      return;
    }
    if (!isReloadingSchema && was && connectionId && database) {
      const final = finalStatusRef.current ?? statusAfterRefresh(connectionId, database);
      finalStatusRef.current = null;
      ownStartPendingRef.current = false;
      // The window is closed: nothing that settles later may speak for it.
      epochRef.current += 1;
      onStatusChange(final.status, final.message);
    }
  }, [isReloadingSchema, connectionId, database, onStatusChange]);

  const reloadSchema = useCallback(() => {
    // The store dedupes concurrent refreshes for the pair: a double-click
    // while the first is still running is a no-op rather than a second
    // initializing→ready flicker.
    if (!connectionId || !database || isSchemaRefreshing(connectionId, database)) return;
    const epoch = ++epochRef.current;
    ownStartPendingRef.current = true;
    finalStatusRef.current = null;
    onStatusChange('initializing', INTELLISENSE_REFRESHING_MESSAGE);

    // `refreshIntelliSense` opens the window synchronously, so the pair's
    // generation read right after it is the one this run started at. When
    // the run settles, exactly ONE more change (its own `end`) is expected;
    // any other number means something else touched the pair — a disconnect
    // clearing the window and a successor re-opening it, even when React
    // batched both into a single render that the effect above never saw as
    // a transition.
    const run = refreshIntelliSense({ connectionId, database, engine: engineId, source: 'pill' });
    const startedAtGeneration = schemaRefreshGeneration(connectionId, database);

    const applyFinal = (final: FinalStatus): void => {
      ownStartPendingRef.current = false;
      // Stale: the tab moved to another pair, another surface opened a
      // window, this run's own window already closed and was resolved by
      // the end transition, or the pair changed hands behind the effect's
      // back — in every case the pill is not ours to set.
      if (epochRef.current !== epoch) return;
      if (schemaRefreshGeneration(connectionId, database) !== startedAtGeneration + 1) return;
      finalStatusRef.current = final;
      onStatusChange(final.status, final.message);
    };

    void run
      .then((outcome) => {
        applyFinal(
          outcome === 'unsupported'
            // Nothing was refreshed, so don't claim a fresh snapshot.
            ? { status: 'ready' }
            : statusAfterRefresh(connectionId, database),
        );
      })
      .catch((error: unknown) => {
        // A refetch that died with the connection is not an error the pill
        // should wear — the connection is simply closed.
        applyFinal(
          isConnectionActive(connectionId)
            ? {
                status: 'error',
                message: error instanceof Error ? error.message : 'Failed to reload database schema',
              }
            : { status: 'unavailable' },
        );
      });
  }, [connectionId, database, engineId, onStatusChange]);

  return { reloadSchema, isReloadingSchema };
}
